import FormBlock from 'components/formComponents/Layout/FormBlock';
import { useMemo } from 'react';
import { useRef } from 'react';
import RequiredConsentBlock from './RequiredConsentBlock';
import ButtonWithIcon from 'components/formComponents/Base/ButtonWithIcon';
import CtaButtonWithIcon from 'components/formComponents/Base/CtaButtonWithIcon';
import TextBlock from 'components/formComponents/Base/TextBlock';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import { useAppSelector } from '../../../../../hooks';
import PrintablePartyApplication from './PrintablePartyApplication';
import { useReactToPrint } from 'react-to-print';
import ExistingBankruptcyContactBlock from './ExistingBankruptcyContactBlock';

type Props = {
  showExistingBankruptcyFlow?: boolean;
  existingBankruptcyIncidentId?: string;
  isCheckingExistingBankruptcyIncident?: boolean;
  existingBankruptcyIncidentLookupFailed?: boolean;
  onExistingBankruptcyContactSubmitted?: () => void;
  onExistingBankruptcyContactSubmittingChange?: (isSubmitting: boolean) => void;
};

const SubmitSection = ({
  showExistingBankruptcyFlow = false,
  existingBankruptcyIncidentId,
  isCheckingExistingBankruptcyIncident = false,
  existingBankruptcyIncidentLookupFailed = false,
  onExistingBankruptcyContactSubmitted,
  onExistingBankruptcyContactSubmittingChange,
}: Props) => {
  const { pageTexts } = useAppSelector((state) => state.partyApplication);
  const printableComponentRef = useRef<HTMLDivElement>(null);
  const texts = useMemo(() => {
    return pageTexts?.submitSection;
  }, [pageTexts]);

  const handlePrint = useReactToPrint({
    documentTitle: 'Ansokan om omstallningsstod',
    content: () => printableComponentRef.current,
  });

  if (showExistingBankruptcyFlow && existingBankruptcyIncidentId) {
    return (
      <FormBlock
        title={
          texts?.existingBankruptcyFlowHeadline ||
          'Befintligt konkursärende hittat'
        }
      >
        <ExistingBankruptcyContactBlock
          incidentId={existingBankruptcyIncidentId}
          texts={texts}
          onSubmitted={() => {
            onExistingBankruptcyContactSubmitted?.();
          }}
          onSubmittingChange={(isSubmitting) => {
            onExistingBankruptcyContactSubmittingChange?.(isSubmitting);
          }}
        />
      </FormBlock>
    );
  }

  return (
    <FormBlock
      title={texts?.submitHeadline ?? 'Bekräfta och skicka in ansökan'}
    >
      <RequiredConsentBlock
        consentCheckboxes={
          texts?.requiredConsentCheckboxes ?? [
            {
              id: 'constentBox1',
              label:
                'Jag intygar att uppgifterna ovan stämmer och godkänner att Trygghetsfonden TSL hanterar mina personuppgifter enligt TSL:s integritetspolicy',
            },
          ]
        }
      />
      {isCheckingExistingBankruptcyIncident && (
        <TextBlock
          type="info"
          text={
            texts?.existingBankruptcyCheckingInfo ??
            'Kontrollerar om det redan finns ett konkursärende...'
          }
        />
      )}
      {existingBankruptcyIncidentLookupFailed && (
        <TextBlock
          type="error"
          text={
            texts?.existingBankruptcyLookupFailedError ??
            'Ett tekniskt fel uppstod vid kontroll av befintligt konkursärende. Försök igen om en stund.'
          }
        />
      )}
      <ButtonWithIcon
        label={texts?.printApplicationBtn ?? 'Skriv ut ansökan'}
        type="button"
        Icon={PrintIcon}
        onClick={handlePrint}
      />
      <CtaButtonWithIcon
        label={texts?.submitApplicationBtn ?? 'Skicka in ansökan'}
        type="submit"
        Icon={SendIcon}
        disabled={
          showExistingBankruptcyFlow || isCheckingExistingBankruptcyIncident
        }
      />
      <div style={{ display: 'none' }}>
        <div ref={printableComponentRef}>
          <PrintablePartyApplication />
        </div>
      </div>
    </FormBlock>
  );
};

export default SubmitSection;
